import { SECOND } from "../utils/time";
import { Gps, Latitude, Longitude, Timestamp, Uid } from "./core";

export interface TrackEntry {
    uid: Uid;
    account: Uid;
    infoText: string;
    dateText: string;
    timeText: string;
    startLat: Latitude;
    startLng: Longitude;
    startTs: Timestamp;
    startText: string;
    startLetter: string;
    endLat: Latitude;
    endLng: Longitude;
    endTs: Timestamp;
    endText: string;
    endLetter: string;
    distance: number;
    published: boolean;
    speeds: number[]; // km/h
    timestamps: number[]; // in seconds
    coordinates?: readonly (readonly number[])[]; // GeoJSON lon/lat, aligned with timestamps
    purpose: string;
    comment: string;
    approvalStatus?: ApprovalStatus;
    approvalTs: Timestamp;
    approvalUid?: Uid;
    /** 0 = recorded by GPS, 1 = entered by hand */
    source?: TrackSource;
    /** a person changed times, endpoints or distance of a GPS trip */
    edited?: boolean;
    /** the requester may change times, endpoints and distance (and delete) */
    editable?: boolean;
    rejectionNote?: string;
    /** "inhouse" | "none" | "" */
    routeSrc?: string;
    /** meters, when the server routed start -> end */
    routedDistance?: number;
    /** seconds */
    routedDuration?: number;
    travelMode?: string;
    /** place uid of the start / end, when the trip was entered against a place */
    startPlace?: Uid;
    endPlace?: Uid;
    creator?: Uid;
    createdTs?: Timestamp;
    updater?: Uid;
    updatedTs?: Timestamp;
    /** GPS values before the first edit; only present when edited */
    original?: TrackOriginal;
}

export enum TrackSource {
    GPS = 0,
    MANUAL = 1,
}

export interface TrackOriginal {
    startTs: Timestamp;
    endTs: Timestamp;
    distance: number;
    startLat: Latitude;
    startLng: Longitude;
    endLat: Latitude;
    endLng: Longitude;
    /** addresses as recorded; empty for trips edited before the server kept them */
    startText?: string;
    endText?: string;
}

export type TrackSourceKind = "gps" | "manual" | "edited";

export const trackSourceKind = (track: Pick<TrackEntry, "source" | "edited">): TrackSourceKind => {
    if (Number(track.source) === TrackSource.MANUAL) {
        return "manual";
    }
    return track.edited ? "edited" : "gps";
}

export const isManualTrack = (track: Pick<TrackEntry, "source">): boolean => Number(track.source) === TrackSource.MANUAL;

export enum ApprovalStatus {
    NONE = 0,
    NOTIFIED = 1,
    READY = 2,
    APPROVED = 3,
    DISAPPROVED = 4,
}

export interface TrackEntryAndData {
    entry: TrackEntry;
    data: [];
}

export interface TrackOrStopEntry extends TrackEntry {
    place?: Uid;
    places?: Uid[];
    job?: string;
    at?: "place" | "job" | "between_tracks" | "idling";
    title?: string;
    waypoint?: Gps;
    color?: string;
    ts?: number;
    letter?: string;
    trackUid?: string;
    kind: "track" | "stop" | "waypoint" | "wp";
    /** a job stop in Location History's Stops list: how the worker checked in, from the job's own record */
    checkIn?: StopCheckIn;
}

/** How a job visit began: by hand or by GPS, and for a manual check-in how far from the job it was made. */
export type StopCheckIn = { manual: boolean; distance?: number; far: boolean };

export const publishedLink = (trackEntry: TrackEntry): string => {
    return trackEntry.published ? "/!/@t/" + trackEntry.account + "/" + trackEntry.uid : ""
}

export const durationMillis = (trackEntry: TrackEntry): number => {
    return trackEntry.startTs > 0 && trackEntry.endTs > 0 ? trackEntry.endTs - trackEntry.startTs : 0;
}

export const averageSpeed = (trackEntry: TrackEntry): number => {
    const meter = trackEntry.distance;
    const seconds = durationMillis(trackEntry) / SECOND;
    return seconds > 0 ? meter / seconds * 3.6 : 0;
}

export const findNearestTimestampIndex = (trackEntry: TrackEntry, targetTsMillis: number): number =>  {
    const targetTS = targetTsMillis / 1000;
    const tsArray = trackEntry.timestamps;
    let nearestIndex = 0;
    let smallestDiff = Math.abs(tsArray[0] - targetTS);

    for (let i = 1; i < tsArray.length; i++) {
        const diff = Math.abs(tsArray[i] - targetTS);
        if (diff < smallestDiff) {
            smallestDiff = diff;
            nearestIndex = i;
        }
    }

    return nearestIndex;
}